import {
    AutoComplete,
    AutoCompleteChangeEvent,
    ListItemProps,
    MultiSelect,
    MultiSelectChangeEvent,
} from '@progress/kendo-react-dropdowns'
import React, { useRef } from 'react'
import { useEffect, useState, useCallback } from 'react'
import Scrollbars from 'react-custom-scrollbars-2'
import { formatContact, trimSemicolons } from '../../utils'
import { validEmail } from '../../GlobalComponents/Validator'

const removeDuplicates = (arr: Array<ContactCustomerAccountCodeViewRow | string>) => {
    let keys: Record<string | number, Boolean> = {}
    let res: typeof arr = []
    arr.forEach((row) => {
        let key = typeof row === 'string' ? row : row.ContactKey
        if (keys[key]) return
        else {
            keys[key] = true
            res.push(row)
        }
    })
    return res
}

const getValidEmail = (raw: string) => {
    return /<(.*)>;?$/.test(raw) ? /<(.*)>;?$/.exec(raw)![1] : trimSemicolons(raw)
}

const getContactsFromEmail = (raw: string, contacts: ContactCustomerAccountCodeView) => {
    let emails = raw.split(';')
    let newContacts: Array<ContactCustomerAccountCodeViewRow | string> = []
    emails.forEach((row) => {
        if (row.length === 0) return
        let existingContact = contacts.find((contact) => contact.EmailAddress === row)
        if (existingContact) newContacts.push(existingContact)
        else {
            if (validEmail(row)) {
                let cleanedEmail = getValidEmail(row)
                let existingContact = contacts.find((row) => row.EmailAddress === cleanedEmail)
                newContacts.push(existingContact || cleanedEmail)
            }
        }
    })
    return newContacts
}

interface EmailSearchBaseProps {
    contacts: ContactCustomerAccountCodeView
    onCreateNewContact(): void
    onInvalid?(): void
    onClear?(): void
    onFocus?(): void
    style?: React.CSSProperties
    invalidOffset?: number
}
interface EmailSearchMultiProps extends EmailSearchBaseProps {
    selectedContacts: Array<ContactCustomerAccountCodeViewRow | string>
    setSelectedContacts(newContacts: Array<ContactCustomerAccountCodeViewRow | string>): void
    multiple: true
}

interface EmailSearchSingleProps extends EmailSearchBaseProps {
    selectedContact: ContactCustomerAccountCodeViewRow | undefined
    setSelectedContact(newContact: ContactCustomerAccountCodeViewRow): void
    multiple?: false
    allowFreeform: false
}
interface EmailSearchSingleFreeformProps extends EmailSearchBaseProps {
    selectedContact: ContactCustomerAccountCodeViewRow | string | undefined
    setSelectedContact(newContact: ContactCustomerAccountCodeViewRow | string): void
    multiple?: false
    allowFreeform: true
}
type EmailSearchProps =
    | EmailSearchMultiProps
    | (EmailSearchSingleFreeformProps | EmailSearchSingleProps)
const EmailSearch = (props: EmailSearchProps) => {
    const {
        contacts,
        onCreateNewContact,
        multiple,
        onClear,
        onInvalid,
        onFocus,
        style,
        invalidOffset,
    } = props

    const [filteredContacts, setFilteredContacts] = useState(contacts)
    const [multiFilter, setMultiFilter] = useState('')
    const [singleFilter, setSingleFilter] = useState('')
    const [invalid, setInvalid] = useState(false)
    const inputRef = useRef<AutoComplete | null>(null)
    // only way the focus is exposed is through an item prom on the itemRenderFunction
    // storing it on here
    const currentFocusedRef = useRef<ContactCustomerAccountCodeViewRow | null>(null)
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Tab' && currentFocusedRef.current) {
                // ignore tab if filter empty
                if (multiple && !multiFilter) return
                if (!multiple && !singleFilter) return
                if (
                    !filteredContacts.find(
                        (contact) => contact.ContactKey === currentFocusedRef.current?.ContactKey
                    )
                )
                    return
                if (props.multiple) {
                    props.setSelectedContacts(
                        removeDuplicates([...props.selectedContacts, currentFocusedRef.current])
                    )
                    setMultiFilter('')
                } else {
                    props.setSelectedContact(currentFocusedRef.current)
                    setSingleFilter(formatContact(currentFocusedRef.current))
                    // window will stay open if we don't blur
                    //@ts-ignore
                    inputRef.current && inputRef.current._input.blur()
                }
            }
        },
        [filteredContacts, multiFilter, multiple, props, singleFilter]
    )

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    const itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        if (itemProps.focused) {
            currentFocusedRef.current = itemProps.dataItem
        }
        if (itemProps.dataItem.ContactKey === currentFocusedRef.current?.ContactKey) {
            currentFocusedRef.current = itemProps.focused ? currentFocusedRef.current : null
        }

        const itemChildren = (
            <span style={{ lineHeight: '1em', padding: 5 }}>
                <div style={{ fontWeight: 500, marginBottom: 5 }}>
                    {`${itemProps.dataItem.FirstName} ${itemProps.dataItem.LastName}`}
                </div>
                <div style={{ fontWeight: 400 }}>{itemProps.dataItem.EmailAddress}</div>
            </span>
        )

        return React.cloneElement(li, li.props, itemChildren)
    }

    useEffect(() => {
        setFilteredContacts(
            contacts.filter((row) => {
                let fullName = `${row.FirstName.trim()} ${row.LastName.trim()} ${row.EmailAddress}`
                return fullName
                    .toUpperCase()
                    .includes((multiple ? multiFilter : singleFilter).toUpperCase())
            })
        )
    }, [singleFilter, multiFilter, multiple, contacts])

    useEffect(() => {
        !props.multiple &&
            props.selectedContact &&
            typeof props.selectedContact === 'object' &&
            setSingleFilter(formatContact(props.selectedContact))
        // @ts-ignore
    }, [props.selectedContact])
    if (props.multiple) {
        let { selectedContacts, setSelectedContacts } = props
        const onMultiChange = (event: MultiSelectChangeEvent) => {
            let newContacts: typeof selectedContacts = event.value
            let newValue = newContacts.find((row) => row === multiFilter)
            // handle semicolon strings
            if (typeof newValue === 'string' && newValue.split(';').length > 1) {
                let emails = newValue.split(';')
                let newContacts: typeof selectedContacts = []
                let invalidEmails = ''
                emails.forEach((row) => {
                    if (row.length === 0) return
                    let existingContact = contacts.find((contact) => contact.EmailAddress === row)
                    if (existingContact) newContacts.push(existingContact)
                    else {
                        if (multiFilter) {
                            if (validEmail(multiFilter)) {
                                let cleanedEmail = getValidEmail(multiFilter)
                                setSelectedContacts(
                                    removeDuplicates([...selectedContacts, cleanedEmail])
                                )
                                setMultiFilter('')
                            } else setInvalid(true)
                        } else invalidEmails += row + ';'
                    }
                })
                setSelectedContacts(removeDuplicates([...selectedContacts, ...newContacts]))
                setMultiFilter(invalidEmails)
                return
            }
            if (typeof newValue === 'string' && !validEmail(newValue)) setMultiFilter(newValue)
            else setSelectedContacts(newContacts)
        }
        return (
            <div style={{ display: 'inline-flex', position: 'relative' }}>
                <div style={style ? style : { minWidth: 400, maxWidth: 600 }}>
                    <Scrollbars
                        className={'scroll-bar'}
                        renderTrackHorizontal={() => <div />}
                        autoHeight
                        autoHeightMin={40}
                        autoHeightMax={70}
                    >
                        <MultiSelect
                            data={filteredContacts}
                            onChange={onMultiChange}
                            value={selectedContacts}
                            filter={multiFilter}
                            filterable={true}
                            onFilterChange={(e) => {
                                setMultiFilter(e.filter.value)
                                setInvalid(false)
                            }}
                            onBlur={(e) => {
                                if (multiFilter.split(';').length > 1) {
                                    let emails = multiFilter.split(';')
                                    let newContacts: typeof selectedContacts = []
                                    let invalidEmails = ''
                                    emails.forEach((row) => {
                                        if (row.length === 0) return
                                        let existingContact = contacts.find(
                                            (contact) => contact.EmailAddress === row
                                        )
                                        if (existingContact) newContacts.push(existingContact)
                                        else {
                                            if (validEmail(row)) {
                                                let cleanedEmail = getValidEmail(row)
                                                let existingContact = contacts.find(
                                                    (row) => row.EmailAddress === cleanedEmail
                                                )
                                                newContacts.push(existingContact || cleanedEmail)
                                            } else invalidEmails += row + ';'
                                        }
                                    })
                                    setSelectedContacts(
                                        removeDuplicates([...selectedContacts, ...newContacts])
                                    )
                                    setMultiFilter(invalidEmails)
                                    invalidEmails && setInvalid(true)

                                    return
                                }

                                let newContact = contacts.find(
                                    (row) => row.EmailAddress === multiFilter
                                )
                                // if the resolve contact is not selected add it
                                if (
                                    newContact &&
                                    !selectedContacts.find(
                                        (row) =>
                                            typeof row !== 'string' &&
                                            row.EmailAddress === newContact?.EmailAddress
                                    )
                                ) {
                                    setSelectedContacts(
                                        removeDuplicates([...selectedContacts, newContact])
                                    )
                                    setMultiFilter('')
                                } else {
                                    if (multiFilter) {
                                        if (validEmail(multiFilter)) {
                                            let cleanedEmail = getValidEmail(multiFilter)
                                            let existingContact = contacts.find(
                                                (row) => row.EmailAddress === cleanedEmail
                                            )
                                            setSelectedContacts(
                                                removeDuplicates([
                                                    ...selectedContacts,
                                                    existingContact || cleanedEmail,
                                                ])
                                            )
                                            setMultiFilter('')
                                        } else {
                                            setInvalid(true)
                                            onInvalid && onInvalid()
                                        }
                                    }
                                }
                            }}
                            tags={selectedContacts.map((row) =>
                                typeof row === 'string'
                                    ? { text: row, data: [row] }
                                    : { text: `${row.FirstName} ${row.LastName}`, data: [row] }
                            )}
                            allowCustom
                            itemRender={itemRender}
                            footer={
                                <span
                                    style={{
                                        marginLeft: 10,
                                        color: 'var(--linkblue)',
                                        cursor: 'pointer',
                                        fontSize: 16,
                                    }}
                                    onMouseDown={onCreateNewContact}
                                >
                                    <span className={'a-i-plus'} />
                                    Create New Contact
                                </span>
                            }
                            style={{ marginBottom: 1 }}
                            valid={!invalid}
                            listNoDataRender={() => null}
                        />
                    </Scrollbars>
                </div>
                {invalid && (
                    <span
                        // style={{
                        //     color: 'red',
                        //     fontWeight: 500,
                        //     margin: 'auto',
                        //     position: 'absolute',
                        //     top: 10,
                        //     right: invalidOffset || 20,
                        // }}
                        style={{
                            color: 'red',
                            fontWeight: 500,
                            margin: 'auto',
                            minWidth: 100,
                            position: 'relative',
                            right: invalidOffset || 0,
                        }}
                    >
                        Invalid Email
                    </span>
                )}
            </div>
        )
    } else {
        let { selectedContact, setSelectedContact } = props

        // you have a contact, you're invalid and your text isn't the formatted contact
        let invalid1 =
            selectedContact &&
            invalid &&
            (typeof selectedContact === 'string'
                ? singleFilter !== selectedContact
                : singleFilter !== formatContact(selectedContact))
        // you don't have a contact, the filter isn't empty and it's an invalid email
        let invalid2 = !selectedContact && singleFilter && invalid

        const onSingleChange = (event: AutoCompleteChangeEvent) => {
            setInvalid(false)
            if (typeof event.value === 'string') {
                setSingleFilter(event.value)
                if (event.value === '') onClear && onClear()
                return
            } else {
                let newContact: ContactCustomerAccountCodeViewRow = event.value
                setSelectedContact(newContact!)
                setSingleFilter(formatContact(newContact!))
            }
        }
        return (
            <div
                style={
                    style
                        ? { display: 'inline-flex', ...style, position: 'relative' }
                        : { display: 'inline-flex', position: 'relative' }
                }
            >
                <AutoComplete
                    data={filteredContacts}
                    onChange={onSingleChange}
                    value={singleFilter}
                    onFocus={(e) => {
                        onFocus && onFocus()
                        if (
                            inputRef.current &&
                            // @ts-ignore
                            document.activeElement === inputRef.current!._input
                        ) {
                            let cValue: string = inputRef.current.value
                            if (
                                !selectedContact ||
                                (typeof selectedContact === 'object' &&
                                    cValue !== formatContact(selectedContact))
                            )
                                return
                            let extensionStart = cValue.indexOf('>')
                            // @ts-ignore
                            extensionStart !== -1 && inputRef.current._input.focus()
                            extensionStart !== -1 &&
                                // @ts-ignore
                                inputRef.current._input.setSelectionRange(0, extensionStart + 1)
                        }
                    }}
                    onBlur={(e) => {
                        let foundContacts = getContactsFromEmail(singleFilter, contacts).filter(
                            (row) => typeof row !== 'string'
                        )
                        if (foundContacts.length > 0) {
                            let existingContact =
                                foundContacts[0] as ContactCustomerAccountCodeViewRow
                            setSelectedContact(existingContact)
                            setSingleFilter(formatContact(existingContact))
                            setInvalid(false)
                            return
                        }

                        let newContact = contacts.find(
                            (row) => row.EmailAddress === trimSemicolons(singleFilter)
                        )
                        if (newContact) {
                            setSelectedContact(newContact)
                            setSingleFilter(formatContact(newContact!))
                        } else {
                            if (validEmail(singleFilter)) {
                                let cleanedEmail = getValidEmail(singleFilter)
                                let existingContact = contacts.find(
                                    (row) => row.EmailAddress === cleanedEmail
                                )
                                if (existingContact) {
                                    setSelectedContact(existingContact)
                                    setSingleFilter(formatContact(existingContact!))
                                }
                                if (props.allowFreeform) {
                                    //TODO should allow string when freeform but that would be a pretty big change and this isn't a sure feature
                                    props.setSelectedContact(cleanedEmail)
                                    return
                                }
                            }
                            setInvalid(true)
                            onInvalid && onInvalid()
                        }
                    }}
                    ref={(ref) => (inputRef.current = ref)}
                    // onChange={(e) => {
                    //     setFilter(e.filter.value)
                    // }}
                    itemRender={itemRender}
                    footer={
                        <span
                            style={{
                                marginLeft: 10,
                                color: 'var(--linkblue)',
                                cursor: 'pointer',
                                fontSize: 16,
                            }}
                            onMouseDown={onCreateNewContact}
                        >
                            <span className={'a-i-plus'} />
                            Create New Contact
                        </span>
                    }
                    style={style || { marginBottom: 1, minWidth: 400, maxWidth: 600 }}
                    // not valid if blur decided you were invalid and the current value is not the formatted selected contact
                    valid={!invalid1 || !invalid2}
                />
                {(invalid1 || invalid2) && (
                    <span
                        style={{
                            color: 'red',
                            fontWeight: 500,
                            margin: 'auto',
                            minWidth: 100,
                            position: 'relative',
                            right: invalidOffset || 0,
                        }}
                    >
                        Invalid Email
                    </span>
                )}
            </div>
        )
    }
}

export default EmailSearch
